import queryString from 'query-string'
import { FC, useEffect, useMemo, useState } from 'react'
import { useAppRouter } from 'src/@legacy/@core/hooks/UseAppRouter'
import { nomarlizeQuery } from 'src/@legacy/utilities/common'
import { LINK_URL_JD_DETAIL } from 'src/@legacy/utilities/LinkURL'
import ResourceList, { ResourceType } from 'src/@legacy/components/ResourceList'
import { fetchJDExamplesList } from 'src/pages/page-jd-examples'
import JDSearch, { JDCateType, JDSearchType } from './JDSearch'
import { JDStyle } from './JDStyle'
import { useClient } from 'src/@legacy/@core/hooks/useClient'
import { API_URL_JD_CATEGORY } from 'src/@legacy/@core/utilities/APIConstant'
import { useTranslation } from 'src/@legacy/@core/hooks/UserTranslation'
import { useTranslateLink } from 'src/@legacy/@optimal-page/hooks/useTranslateLink'

export type JDType = {
  date_created: string
  date_updated: string
  id: number
  id_category: number
  jd_name: string
  jd_name_vi: string
  job_description_content: string
  job_description_content_vi: string
  first_description: string
  first_description_vi: string
  meta_description: string
  meta_description_vi: string
  meta_title: string
  meta_title_vi: string
  requirement_content: string
  requirement_content_vi: string
  skills: string[]
  slug: string
  slug_vi: string
  sort: string
  status: string
  summary: string
  summary_vi: string
  user_created: string
  user_updated: string
  key_words: string
} & ResourceType

const groupBy = (arr: JDType[], cates: JDCateType[], currentLanguage?: string): { [char: string]: JDType[] } => {
  return arr.reduce((group, jd) => {
    const { id_category } = jd
    const filterCategory = cates?.filter((cate) => cate.id === id_category)[0]
    const cate_name = currentLanguage === 'en' ? filterCategory?.categories_name : filterCategory?.categories_name_vi

    return cate_name
      ? {
          ...group,
          ...{
            [cate_name]: [
              ...(group[cate_name] || []),
              {
                ...jd,
                ...{
                  title: currentLanguage === 'en' ? jd.jd_name : jd.jd_name_vi,
                  link: LINK_URL_JD_DETAIL(jd.slug),
                  link_vi: LINK_URL_JD_DETAIL(jd.slug_vi)
                }
              }
            ]
          }
        }
      : group
  }, {})
}

const JDWrapper: FC<{ JDData: JDType[] }> = ({ JDData }) => {
  const { client } = useClient()
  const { translate, currentLanguage } = useTranslation()
  const router = useAppRouter()
  const query = nomarlizeQuery(router.query)
  const [JDCateList, setJDCateList] = useState<JDCateType[]>()
  const [JDList, setJDList] = useState<JDType[]>(JDData)
  const filter = useMemo(
    () => ({
      search: query.search !== '' && query.search !== undefined ? query.search : undefined,
      category: query.category !== '' && query.category !== undefined ? query?.category : undefined
    }),
    [query]
  )

  const groupJD = useMemo(() => (JDList ? groupBy(JDList, JDCateList, currentLanguage) : {}), [JDList, JDCateList, currentLanguage])

  const pushSearch = (value: JDSearchType) => {
    const q = queryString.stringify(value, { arrayFormat: 'bracket', decode: false, encode: false }) || ''
    router.push(router.asPath.split('?')[0] + '?' + q, router.asPath.split('?')[0] + '?' + q, { scroll: false })
  }

  const fetchJDCates = () => {
    return client.get(`${API_URL_JD_CATEGORY}?filter[status][_eq]=published`, {}).then((rs) => setJDCateList(rs.data.data))
  }

  useEffect(() => {
    fetchJDCates()
  }, [])

  useEffect(() => {
    if (JDList) {
      let queryString = ''
      if (filter.search) queryString = `&search=${filter.search}`
      if (filter.category) queryString += `&filter[_and][0][_and][0][id_category][_eq]=${filter.category}`
      fetchJDExamplesList(queryString).then((data) => setJDList(data))
    }
  }, [filter.category, filter.search])

  return (
    <>
      <style jsx>{JDStyle}</style>
      <div className="banner">
        <h1 className="banner__title">{translate('resources:jd_500_examples')}</h1>
        <div className="banner__summary">{translate('resources:jd_better_job_description')}</div>
      </div>
      <div className="wrapper-container">
        <div className="jd-wrapper">
          <JDSearch
            initialValues={filter}
            onSubmit={pushSearch}
            JDCateList={JDCateList}
            translate={translate}
            currentLanguage={currentLanguage}
          />

          <ResourceList groupData={groupJD} />
        </div>
      </div>
    </>
  )
}

export default JDWrapper
